'use client';

import { useEffect, useState } from 'react';
import LegislationDecisionHistory from './legislation-decision-history';

interface LegislationDecisionDetectorProps {
  iri: string;
}

/**
 * Viewports that get the margin badges at all.
 *
 * The badges are absolutely positioned in a right-hand gutter that only exists
 * on desktop, so this mirrors the `isMobile` check the parent used to make.
 * It lives HERE rather than in the parent's render because the parent's check
 * ran in an effect seeded `false`, i.e. every phone mounted this component,
 * paid for the scan below, and then unmounted it — the scan is the expensive
 * part and it was being paid on exactly the device that cannot show the result.
 */
const DESKTOP_QUERY = '(min-width: 769px)';

const LegislationDecisionDetector = ({
  iri,
}: LegislationDecisionDetectorProps) => {
  const [elements, setElements] = useState<{ id: string; top: number }[]>([]);
  const [rightOffset, setRightOffset] = useState(24);

  useEffect(() => {
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    const scan = () => {
      const container = document.querySelector('.legislation-content');
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      const containerAbsoluteTop = containerRect.top + window.scrollY;

      // Align badges with the right edge of the 900px content area
      const slovLex = container.querySelector('.slov-lex-style');
      if (slovLex) {
        const slovLexRect = slovLex.getBoundingClientRect();
        setRightOffset(Math.max(0, containerRect.right - slovLexRect.right));
      }

      const elems = document.querySelectorAll(
        '.slov-lex-style [id]:not([id*="text"]):not([id="iri"]):not([id="predpis"]):not([id*="oznacenie"]):not([id*="typ"]):not([id*="podnadpis"]):not([id*="datum"]):not([id*="nadpis"])'
      );

      setElements(
        Array.from(elems).map((elem) => {
          const id = elem.id;
          const rect = elem.getBoundingClientRect();
          // Calculate the element's absolute top position
          const absoluteTop = rect.top + window.scrollY;
          // Compute position relative to the container
          const relativeTop = absoluteTop - containerAbsoluteTop;
          return {
            id,
            top: relativeTop,
          };
        })
      );
    };

    // Run the scan when the main thread is free rather than in the commit that
    // follows hydration. It walks every id-bearing node in a statute — bodies
    // here reach ~458k characters — and reads a rect from each, so on a phone-
    // class CPU it is long enough to sit in front of the user's first tap and
    // be counted as that tap's input delay. Nothing painted depends on it: the
    // badges are an absolutely-positioned overlay, so arriving a frame late is
    // invisible, while blocking for it is not.
    const idle = window.requestIdleCallback;
    if (typeof idle === 'function') {
      const handle = idle(scan, { timeout: 2_000 });
      return () => window.cancelIdleCallback(handle);
    }

    // Safari has no requestIdleCallback; a macrotask still yields to any
    // pending input before running.
    const handle = window.setTimeout(scan, 200);
    return () => window.clearTimeout(handle);
  }, []);

  return (
    <>
      {elements.length > 0 &&
        iri &&
        elements.map(({ id, top }) => {
          const excludeSubstrings = ['.nadpis'];
          const parts = iri.split('/');
          const refId = parts.slice(0, 4).join('/');

          const refIdWithId = `/${refId}/#${id}`;
          if (!excludeSubstrings.some((substring) => id.includes(substring))) {
            return (
              <LegislationDecisionHistory
                key={id}
                top={top}
                rightOffset={rightOffset}
                refIdWithId={refIdWithId}
                iri={`/${iri}`}
                elementId={id}
              />
            );
          }
          return null; // Explicit return for filtered-out elements
        })}
    </>
  );
};

export default LegislationDecisionDetector;
